import React, { useEffect, useState } from 'react';
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Loader2, Link as LinkIcon, FileText, StickyNote, HelpCircle } from 'lucide-react';
import { toast } from 'sonner';
import { quickAddEvidence } from '../api/client';

// One dialog serves four flows so the assessor sees only the fields relevant
// to what they're adding. Behaviour per mode:
//   research      → URL / PDF / image + title / organisation / accessed date / extracted text / note
//   technical_doc → PDF or URL + title / manufacturer / note (no accessed date)
//   site_note     → short text OR small file (photo of scribbled note) + note
//   assumption    → reason for the default, no file — evidence-of-record type
//
// When `outcome` is supplied we link the new record to it immediately and the
// assessor stays on the outcomes screen.

const MODE_META = {
  research: {
    title: 'Add Research',
    Icon: LinkIcon,
    accept: 'application/pdf,image/*',
    showUrl: true,
    showFile: true,
    showTitle: true,
    showOrg: true,
    orgLabel: 'Organisation / source',
    showAccessed: true,
    showExtracted: true,
    showNote: true,
    submitLabel: 'Add research',
    helpText: 'Paste a URL (recommended) and/or upload a PDF or screenshot. Add the section that supports the outcome.',
  },
  technical_doc: {
    title: 'Add Technical Document',
    Icon: FileText,
    accept: 'application/pdf,image/*',
    showUrl: true,
    showFile: true,
    showTitle: true,
    showOrg: true,
    orgLabel: 'Manufacturer',
    showAccessed: false,
    showExtracted: true,
    showNote: true,
    submitLabel: 'Add document',
    helpText: 'Upload the manufacturer/spec sheet or link to it. Note the page or figure the outcome comes from.',
  },
  site_note: {
    title: 'Add Site Note',
    Icon: StickyNote,
    accept: 'image/*',
    showUrl: false,
    showFile: true,
    showTitle: false,
    showOrg: false,
    showAccessed: false,
    showExtracted: false,
    showNote: true,
    showAsPrimaryNote: true,
    submitLabel: 'Add note',
    helpText: 'A short site note. Optionally attach a photograph of the scribbled note.',
  },
  assumption: {
    title: 'Record Assumption',
    Icon: HelpCircle,
    accept: null,
    showUrl: false,
    showFile: false,
    showTitle: false,
    showOrg: false,
    showAccessed: false,
    showExtracted: false,
    showAssumption: true,
    showNote: false,
    submitLabel: 'Record assumption',
    helpText: 'Record the default or reasonable assumption you have applied and the reason for it. This is never treated as physical evidence — it is provenance for a defended assumption.',
  },
};

export const AddQuickEvidenceDialog = ({
  open, onOpenChange, projectId, outcome, mode = 'research', onAdded,
}) => {
  const meta = MODE_META[mode] || MODE_META.research;
  const [url, setUrl] = useState('');
  const [file, setFile] = useState(null);
  const [title, setTitle] = useState('');
  const [org, setOrg] = useState('');
  const [accessed, setAccessed] = useState(() => new Date().toISOString().slice(0, 10));
  const [extractedText, setExtractedText] = useState('');
  const [note, setNote] = useState('');
  const [assumptionReason, setAssumptionReason] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open) return;
    setUrl('');
    setFile(null);
    setTitle('');
    setOrg('');
    setAccessed(new Date().toISOString().slice(0, 10));
    setExtractedText('');
    setNote('');
    setAssumptionReason('');
  }, [open, mode]);

  const submit = async () => {
    const fd = new FormData();
    fd.append('evidence_kind', mode);
    if (outcome?.id) fd.append('outcome_id', outcome.id);
    if (file) fd.append('file', file);
    if (meta.showUrl && url.trim()) fd.append('url', url.trim());
    if (meta.showTitle && title.trim()) fd.append('title', title.trim());
    if (meta.showOrg && org.trim()) fd.append('organisation', org.trim());
    if (meta.showAccessed && accessed) fd.append('accessed_date', accessed);
    if (meta.showExtracted && extractedText.trim()) fd.append('extracted_text', extractedText.trim());
    // For site notes we treat the "note" as the primary content of the record.
    if (meta.showNote && note.trim()) fd.append('note', note.trim());
    if (meta.showAsPrimaryNote && note.trim() && !file) {
      // Extra: also stored in extracted_text so it renders as first-class content in future views.
      fd.append('extracted_text', note.trim());
    }
    if (meta.showAssumption && assumptionReason.trim()) fd.append('assumption_reason', assumptionReason.trim());

    // Cheap client-side guards for clearer UX than a bare 400.
    if (mode === 'research' && !url.trim() && !file && !extractedText.trim()) {
      toast.error('Add a URL, upload a file, or paste the supporting text.');
      return;
    }
    if (mode === 'technical_doc' && !file && !url.trim()) {
      toast.error('Add a PDF/image or a URL to the technical document.');
      return;
    }
    if (mode === 'site_note' && !note.trim() && !file) {
      toast.error('Add a short note or attach a photo of the site note.');
      return;
    }
    if (mode === 'assumption' && !assumptionReason.trim()) {
      toast.error('Describe the assumption/default that has been applied.');
      return;
    }

    setSubmitting(true);
    try {
      const result = await quickAddEvidence(projectId, fd);
      toast.success(`${meta.title.replace('Add ', '').replace('Record ', '')} added${outcome?.id ? ' and linked' : ''}.`);
      onAdded?.(result);
      onOpenChange(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to add evidence');
    } finally {
      setSubmitting(false);
    }
  };

  const Icon = meta.Icon;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="bg-slate-900 border-slate-800 text-slate-100 max-w-2xl max-h-[90vh] overflow-hidden flex flex-col"
        data-testid={`quick-add-${mode}`}
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Icon className="w-4 h-4 text-emerald-400" />
            {meta.title}
            {outcome && (
              <span className="text-xs text-slate-400 font-normal ml-1">
                → {outcome.label}
                {outcome.value !== null && outcome.value !== undefined && (
                  <span className="ml-1 font-mono text-slate-500">= {String(outcome.value)}{outcome.unit ? ' ' + outcome.unit : ''}</span>
                )}
              </span>
            )}
          </DialogTitle>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto py-3 space-y-3 text-sm">
          <p className="text-xs text-slate-400 leading-relaxed">{meta.helpText}</p>

          {meta.showUrl && (
            <div>
              <Label className="text-[10px] uppercase tracking-widest text-slate-500">Source URL</Label>
              <Input
                data-testid="quick-add-url"
                value={url}
                onChange={(e) => setUrl(e.target.value)}
                placeholder="https://..."
                className="mt-1 bg-slate-950 border-slate-700 h-9"
                type="url"
              />
            </div>
          )}

          {meta.showFile && (
            <div>
              <Label className="text-[10px] uppercase tracking-widest text-slate-500">
                {mode === 'site_note' ? 'Photo of the note (optional)' : 'File (PDF or image, optional if URL provided)'}
              </Label>
              <input
                data-testid="quick-add-file"
                type="file"
                accept={meta.accept}
                onChange={(e) => setFile(e.target.files?.[0] || null)}
                className="mt-1 block w-full text-xs text-slate-300 file:mr-2 file:py-1.5 file:px-3 file:rounded file:border-0 file:bg-slate-700 file:text-slate-100 hover:file:bg-slate-600"
              />
              {file && (
                <div className="text-[11px] text-slate-500 mt-1">
                  {file.name} — {(file.size / 1024).toFixed(0)} KB
                </div>
              )}
            </div>
          )}

          {meta.showTitle && (
            <div>
              <Label className="text-[10px] uppercase tracking-widest text-slate-500">Title</Label>
              <Input
                data-testid="quick-add-title"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder={mode === 'technical_doc' ? 'e.g. GreenStar 30i datasheet' : 'e.g. ADL2 2010 U-value tables'}
                className="mt-1 bg-slate-950 border-slate-700 h-9"
              />
            </div>
          )}

          {meta.showOrg && (
            <div>
              <Label className="text-[10px] uppercase tracking-widest text-slate-500">{meta.orgLabel}</Label>
              <Input
                data-testid="quick-add-org"
                value={org}
                onChange={(e) => setOrg(e.target.value)}
                placeholder={mode === 'technical_doc' ? 'e.g. Worcester Bosch' : 'e.g. HM Government'}
                className="mt-1 bg-slate-950 border-slate-700 h-9"
              />
            </div>
          )}

          {meta.showAccessed && (
            <div>
              <Label className="text-[10px] uppercase tracking-widest text-slate-500">Accessed date</Label>
              <Input
                data-testid="quick-add-accessed"
                type="date"
                value={accessed}
                onChange={(e) => setAccessed(e.target.value)}
                className="mt-1 bg-slate-950 border-slate-700 h-9"
              />
            </div>
          )}

          {meta.showExtracted && (
            <div>
              <Label className="text-[10px] uppercase tracking-widest text-slate-500">Relevant excerpt (optional)</Label>
              <Textarea
                data-testid="quick-add-extracted"
                value={extractedText}
                onChange={(e) => setExtractedText(e.target.value)}
                placeholder="Paste the specific line/paragraph that supports the outcome."
                rows={3}
                className="mt-1 bg-slate-950 border-slate-700 resize-none"
              />
            </div>
          )}

          {meta.showAssumption && (
            <div>
              <Label className="text-[10px] uppercase tracking-widest text-slate-500">Assumption / default applied</Label>
              <Textarea
                data-testid="quick-add-assumption"
                value={assumptionReason}
                onChange={(e) => setAssumptionReason(e.target.value)}
                placeholder='e.g. "No U-value certificate available for cavity wall — default 0.35 W/m²K applied per iSBEM 2010 Table 4."'
                rows={4}
                className="mt-1 bg-slate-950 border-slate-700 resize-none"
              />
            </div>
          )}

          {meta.showNote && (
            <div>
              <Label className="text-[10px] uppercase tracking-widest text-slate-500">
                {mode === 'site_note' ? 'Site note' : 'Assessor note (optional)'}
              </Label>
              <Textarea
                data-testid="quick-add-note"
                value={note}
                onChange={(e) => setNote(e.target.value)}
                placeholder={mode === 'site_note'
                  ? 'e.g. "TRVs fitted to all radiators (visible in P011 & P012)."'
                  : 'Anything to note when this evidence is reviewed later.'}
                rows={mode === 'site_note' ? 3 : 2}
                className="mt-1 bg-slate-950 border-slate-700 resize-none"
              />
            </div>
          )}
        </div>

        <DialogFooter className="border-t border-slate-800 pt-3 gap-2">
          <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={submitting}>
            Cancel
          </Button>
          <Button
            onClick={submit}
            disabled={submitting}
            data-testid={`quick-add-submit-${mode}`}
            className="bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-medium"
          >
            {submitting && <Loader2 className="w-3.5 h-3.5 animate-spin mr-2" />}
            {meta.submitLabel}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
